// World time on the client (docs/23): the `world_clock` row (fk-world clock.rs) read as a date,
// and the small helpers the World screen's almanac, the encounter HUD and the Saga share. The
// module keeps the calendar; nothing here advances it (a finished fight or the storyteller's time
// jump does, docs/24 K1).

export type SeasonKey = 'spring' | 'summer' | 'autumn' | 'winter';
export const SEASON_KEYS: readonly SeasonKey[] = ['spring', 'summer', 'autumn', 'winter'];
export type Biome = 'temperate' | 'wetland' | 'boreal' | 'desert' | 'highland' | 'jungle' | 'underdark' | 'ashlands';
export const WEEKS_PER_SEASON = 12;

/** The fields of a `world_clock` row the client reads. */
export interface ClockRow {
  year: number;
  season: number;
  week: number;
  seasonName: string;
  seasonNames: string[];
  reckoning: string;
  biome: string;
  temperature: string;
  climate: string;
  /** The realm's prevailing weather this week (docs/24 K5), a `SKY_WORDS` key. */
  weather: string;
  weekIndex: bigint;
  startIndex: bigint;
}

export const seasonKey = (i: number | undefined): SeasonKey | undefined => (i === undefined || i < 0 || i > 3 ? undefined : SEASON_KEYS[i]);
/** "winter" → 'winter'; anything else → undefined (review overrides such as `?sky=`). */
export const parseSeasonKey = (s: string | undefined): SeasonKey | undefined => (SEASON_KEYS as readonly string[]).includes((s ?? '').toLowerCase()) ? ((s ?? '').toLowerCase() as SeasonKey) : undefined;

/** "1st", "2nd", "3rd", "11th", "22nd". */
export function ordinal(n: number): string {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
}

/** "2nd week of Bloomtide". */
export const weekPhrase = (c: Pick<ClockRow, 'week' | 'seasonName'>): string => `${ordinal(c.week)} week of ${c.seasonName}`;

/** "3020 After the Drowning" (or just the year). */
export const yearPhrase = (c: Pick<ClockRow, 'year' | 'reckoning'>): string => (c.reckoning.trim() ? `${c.year} ${c.reckoning.trim()}` : `${c.year}`);

/** "Bloomtide · week 2 · 3020", for plates and menu lines. */
export const shortDate = (c: Pick<ClockRow, 'seasonName' | 'week' | 'year'>): string => `${c.seasonName} · week ${c.week} · ${c.year}`;

/** "2nd week of Bloomtide, 3020 After the Drowning". */
export const dateLine = (c: ClockRow): string => `${weekPhrase(c)}, ${yearPhrase(c)}`;

/** How long the story has run: "the first week", "3 weeks", "1 year, 5 weeks". */
export function elapsed(c: Pick<ClockRow, 'weekIndex' | 'startIndex'>): string {
  const w = Number(c.weekIndex - c.startIndex);
  if (w <= 0) return 'the first week';
  const years = Math.floor(w / 48), weeks = w % 48;
  const ws = weeks ? `${weeks} week${weeks === 1 ? '' : 's'}` : '';
  return years ? `${years} year${years === 1 ? '' : 's'}${ws ? `, ${ws}` : ''}` : ws;
}

/** Where in the year we are, 0..1 (spring week 1 = 0). */
export const yearFraction = (c: Pick<ClockRow, 'season' | 'week'>): number => (c.season * WEEKS_PER_SEASON + (c.week - 1)) / (4 * WEEKS_PER_SEASON);

/** "temperate" → "Temperate country". */
export const BIOME_WORDS: Record<string, string> = {
  temperate: 'Temperate woods and meadows',
  wetland: 'Fens, meres and drowned woods',
  boreal: 'Northern pine forest',
  desert: 'Desert dunes and oases',
  highland: 'Moors and high crags',
  jungle: 'Steaming jungle',
  underdark: 'The Underdark',
  ashlands: 'The Ashlands',
};

/** Under the rock (fk-schema `Biome::underground`, docs/23 Y9): no sky, sun, moon or wind. */
export const isUnderground = (biome: string | undefined): boolean => biome === 'underdark';
/** What each real season is called under the rock (fk-schema `underdark_season`). */
export const DEEP_SEASON_WORDS: Record<SeasonKey, string> = { spring: 'The seep', summer: 'The bloom', autumn: 'The spore-fall', winter: 'The deep still' };
/** Where the water is lava (fk-schema `Biome::molten`, docs/23 Y10): the Ashlands. */
export const isMolten = (biome: string | undefined): boolean => biome === 'ashlands';
/** What each real season is called in the Ashlands (fk-schema `ashlands_season`). */
export const ASH_SEASON_WORDS: Record<SeasonKey, string> = { spring: 'The waking', summer: 'The burning', autumn: 'The ashfall', winter: 'The ash winter' };
/** "Spring", or "The seep" in the Underdark, "The ashfall" in the Ashlands. */
export const seasonWordIn = (s: SeasonKey, biome: string | undefined): string =>
  isUnderground(biome) ? DEEP_SEASON_WORDS[s] : isMolten(biome) ? ASH_SEASON_WORDS[s] : s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The region kit the overworld chart is painted in (docs/23 Y5, the client's `kits.ts`): the
 * realm's open country as it looks this season — a temperate valley is gold in autumn and white
 * in winter, the desert always dunes. Mirrors fk-schema `grounds(biome, season)[0]`, except that
 * the wetland chart stays green (the bog kit's mud reads as a flood at chart scale).
 */
export function overworldKit(biome: string | undefined, season: SeasonKey | undefined): string {
  const s = season ?? 'summer';
  switch (biome) {
    case 'temperate': return s === 'autumn' ? 'autumn' : s === 'winter' ? 'winterwood' : 'forest';
    case 'wetland': return s === 'autumn' ? 'autumn' : s === 'winter' ? 'winterwood' : 'forest';
    case 'boreal': return s === 'winter' ? 'winterwood' : 'pinewood';
    case 'desert': return 'dunes';
    case 'highland': return s === 'winter' ? 'winterwood' : 'highland';
    case 'jungle': return 'jungle';
    case 'underdark': return 'underdark';
    case 'ashlands': return 'ashlands';
    default: return 'forest';
  }
}

/** A word for the weather the module stamped (`kind[:intensity[:wind]]`), for sky lines. */
export const SKY_WORDS: Record<string, string> = {
  clear: 'clear', overcast: 'grey skies', rain: 'rain', storm: 'a storm', fog: 'fog', snow: 'snow',
  heat: 'scorching heat', sandstorm: 'a sandstorm', blizzard: 'a blizzard', frost: 'hard frost', sleet: 'sleet',
};
/** The same under the rock (fk-schema `weather_words`, docs/23 Y9): rain is the seep, fog spore haze. */
export const DEEP_SKY_WORDS: Record<string, string> = {
  clear: 'still air', overcast: 'a heavy gloom', rain: 'the seep dripping from the roof', fog: 'mist and spore haze',
  heat: 'close hot air off the deep vents', frost: 'rime creeping down from the surface',
};
/** The same in the Ashlands (fk-schema `weather_words`, docs/23 Y10): rain is black with ash, a sandstorm an ash storm. */
export const ASH_SKY_WORDS: Record<string, string> = {
  clear: 'a hard red sun through thin ash', overcast: 'a pall of ash across the sky', rain: 'black rain thick with ash',
  storm: 'fire storms of ash and lightning', fog: 'sulphur reek and vent steam', heat: 'searing heat off the lava',
  sandstorm: 'ash storms', frost: 'hoarfrost on the black glass',
};
/** A weather in the country's own words. */
export const skyWords = (weather: string, biome: string | undefined): string =>
  (isUnderground(biome) ? DEEP_SKY_WORDS[weather] : isMolten(biome) ? ASH_SKY_WORDS[weather] : undefined) ?? SKY_WORDS[weather] ?? weather;

/** An hour in the country's own words (fk-schema `hour_words_in`): under the rock, the glow's hours; in the Ashlands, the red day and the fire-dark. */
export function hourWordsIn(h: number, biome: string | undefined): string {
  const x = ((Math.floor(h) % 24) + 24) % 24;
  if (isMolten(biome)) {
    if (x <= 3) return 'the depth of the fire-dark';
    if (x <= 7) return 'a blood-red dawn through the ash';
    if (x <= 16) return 'the red day';
    if (x <= 20) return 'the ember dusk';
    return 'the fire-dark';
  }
  if (!isUnderground(biome)) return hourWords(h);
  if (x <= 3) return 'the depth of the deep dark';
  if (x <= 7) return 'the waking of the glow';
  if (x <= 15) return 'the full glow';
  if (x <= 19) return 'the fading of the glow';
  return 'the deep dark';
}

/** "dawn", "noon", "dusk" … for an hour 0–23 (fk-schema `hour_words`). */
export function hourWords(h: number): string {
  const x = ((Math.floor(h) % 24) + 24) % 24;
  if (x === 0) return 'midnight';
  if (x <= 3) return 'the dead of night';
  if (x <= 5) return 'first light';
  if (x <= 7) return 'dawn';
  if (x <= 10) return 'morning';
  if (x <= 12) return 'noon';
  if (x <= 15) return 'afternoon';
  if (x <= 17) return 'late afternoon';
  if (x <= 19) return 'dusk';
  if (x <= 21) return 'nightfall';
  return 'night';
}
